import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useTranslation } from '@hooks';
import Drawer from '@components/Drawer';
import ExpandedSection from '@components/ExpandedSection';
import { LocationIntegrationName } from '@constants/integrations';
import { ROLE } from '@constants/user';
import {
    alertActions,
    coverManagerActions,
    accountActions,
    facebookActions,
    googleActions,
    vtexActions,
    locationsProfilesActions,
} from '@actions';
import {
    coverManagerIntegrationService,
    facebookIntegrationService,
    googleIntegrationService,
    vtexIntegrationService,
} from '@services';
import { handleApiError } from '@helpers/api';
import { objectIsEmpty } from '@helpers/objectIsEmpty';
import LocationCard from './LocationCard';
import GoogleLocationCard from './GoogleLocationCard';

const ExpandedSectionWrapper = styled(ExpandedSection)({
    marginBottom: '16px',
});

const GenericIntegration = ({ onClose, onConnect, isOpen, selectedCompany, setSelectedCompany, integrationType }) => {
    const { t } = useTranslation();
    const [location, setLocation] = useState({});
    const [section, setSection] = useState({});

    const account = useSelector(state => state.account.account);
    const user = useSelector(state => state.auth.user);

    const isAdmin = user.role === ROLE.ADMIN;
    const isSuperAdmin = user?.role === ROLE.SUPER_ADMIN;

    const data = {
        [LocationIntegrationName.CoverManager]: useSelector(state => state.coverManager.data),
        [LocationIntegrationName.Facebook]: useSelector(state => state.facebook.data),
        [LocationIntegrationName.Google]: useSelector(state => state.google.data),
        [LocationIntegrationName.VTEX]: useSelector(state => state.vtex.data),
    };
    const LOCATIONS = data[integrationType];
    const history = useHistory();
    const dispatch = useDispatch();

    const dataFetchMachine = {
        [LocationIntegrationName.CoverManager]: coverManagerActions.get,
        [LocationIntegrationName.Facebook]: facebookActions.getPages,
        [LocationIntegrationName.Google]: googleActions.get,
        [LocationIntegrationName.VTEX]: vtexActions.get,
    };

    const dataResetMachine = {
        [LocationIntegrationName.CoverManager]: coverManagerActions.reset,
        [LocationIntegrationName.Facebook]: facebookActions.reset,
        [LocationIntegrationName.Google]: googleActions.reset,
        [LocationIntegrationName.VTEX]: vtexActions.reset,
    };

    const companiesByAccount = useMemo(() => {
        return Object.groupBy(LOCATIONS, l => l.accountName);
    }, [LOCATIONS]);

    useEffect(() => {
        if (!LOCATIONS.length) {
            dispatch(dataFetchMachine[integrationType]());
        }
    }, [LOCATIONS, dispatch]);

    useEffect(() => {
        if (LOCATIONS?.length) {
            const availableLocation = LOCATIONS.find(l => !l.connected);
            if (availableLocation) {
                setLocation(availableLocation);
            }
            if (integrationType === LocationIntegrationName.Google) {
                setSection(
                    Object.fromEntries(
                        [...new Set(LOCATIONS.map(l => l.accountName))].map(accountName => [accountName, true]),
                    ),
                );
            }
        }
    }, [LOCATIONS]);

    const INTEGRATION_CONNECTED = account?.integrations.find(i => i.type === integrationType)?.isActive;

    const callback = {
        [LocationIntegrationName.CoverManager]: data => coverManagerIntegrationService.editProfile(data),
        [LocationIntegrationName.Facebook]: data => facebookIntegrationService.editProfile(data),
        [LocationIntegrationName.Google]: data => googleIntegrationService.editProfile(data),
        [LocationIntegrationName.VTEX]: data => vtexIntegrationService.editProfile(data),
    };

    const integrationIdProp = {
        [LocationIntegrationName.CoverManager]: 'slug',
        [LocationIntegrationName.Facebook]: 'id',
        [LocationIntegrationName.Google]: 'locationId',
        [LocationIntegrationName.VTEX]: 'accountName',
    };

    const goToIntegrations = () => history.push('/account-settings/integrations');

    const onConnectIntegration = async () => {
        const data = {
            locationId: selectedCompany.companyId,
            ...(integrationType === LocationIntegrationName.CoverManager
                ? {
                      restaurantSlug: location.slug,
                      name: location.name,
                  }
                : {}),
            ...(integrationType === LocationIntegrationName.Facebook
                ? {
                      pageId: location.id,
                      name: location.name,
                      username: location.username,
                  }
                : {}),
            ...(integrationType === LocationIntegrationName.Google
                ? {
                      googlePlaceId: location.placeId || null,
                      googleAccountId: location.accountId || null,
                      googleLocationId: location.locationId || null,
                      name: location.title,
                  }
                : {}),
            ...(integrationType === LocationIntegrationName.VTEX
                ? {
                      accountName: location.accountName || null,
                  }
                : {}),
        };
        try {
            onClose();
            const updatedCompany = await callback[integrationType](data);
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(dataResetMachine[integrationType]());
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (!INTEGRATION_CONNECTED && isSuperAdmin) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        } else if (INTEGRATION_CONNECTED && (isSuperAdmin || isAdmin)) {
            return {
                primaryAction: onConnectIntegration,
                primaryText: t('buttons.connect'),
                primaryDisabled: objectIsEmpty(location),
            };
        } else if (!INTEGRATION_CONNECTED && !isSuperAdmin) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: true,
            };
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                INTEGRATION_CONNECTED
                    ? t('SettingsCompanies.connectLocation')
                    : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                INTEGRATION_CONNECTED
                    ? t('SettingsCompanies.connectDrawerDescription')
                    : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
            secondaryText={INTEGRATION_CONNECTED && (isSuperAdmin || isAdmin) ? t('buttons.cancel') : null}
        >
            {INTEGRATION_CONNECTED ? (
                integrationType === LocationIntegrationName.Google ? (
                    Object.entries(companiesByAccount).map(([accountName, locations]) => (
                        <ExpandedSectionWrapper
                            key={accountName}
                            isOpen={section[accountName]}
                            onClick={() => setSection(prev => ({ ...prev, [accountName]: !prev[accountName] }))}
                            text={accountName}
                        >
                            <div style={{ display: 'grid', gridGap: '8px' }}>
                                {locations.map(item => {
                                    return (
                                        <GoogleLocationCard
                                            key={item.name}
                                            location={item}
                                            selectedLocation={location}
                                            setLocation={setLocation}
                                        />
                                    );
                                })}
                            </div>
                        </ExpandedSectionWrapper>
                    ))
                ) : (
                    <div style={{ display: 'grid', gridGap: '8px' }}>
                        {LOCATIONS.map(item => (
                            <LocationCard
                                key={item.id}
                                location={item}
                                selectedLocation={location}
                                setLocation={setLocation}
                                integrationType={integrationType}
                                integrationIdProp={integrationIdProp[integrationType]}
                                title={integrationType === LocationIntegrationName.VTEX ? item.accountName : item.name}
                            />
                        ))}
                    </div>
                )
            ) : null}
        </Drawer>
    );
};

export default GenericIntegration;
